import { useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import SectionCard from '@/components/base/SectionCard';
import StatusBadge from '@/components/base/StatusBadge';
import { dnsRecords } from '@/mocks/branding';
import type { DomainStatus } from '@/types/portal';

interface DomainCardProps {
  domain: string;
  status: DomainStatus;
  canEdit: boolean;
  /** Shown under the domain field, e.g. when verification fails. */
  error?: string;
  onDomain: (value: string) => void;
  onVerify: () => void;
}

export default function DomainCard({
  domain,
  status,
  canEdit,
  error,
  onDomain,
  onVerify,
}: DomainCardProps) {
  const [copied, setCopied] = useState('');

  const copy = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(id);
      window.setTimeout(() => setCopied(''), 1600);
    } catch {
      setCopied('');
    }
  };

  const verified = status === 'Verified';

  return (
    <SectionCard
      title="Client-facing domain"
      subtitle="Serve the portal, client views and creator links from your own domain"
      action={<StatusBadge status={status} />}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="brand-domain" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
            Custom domain
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-global-line text-foreground-400" />
            <input
              id="brand-domain"
              type="text"
              value={domain}
              disabled={!canEdit}
              onChange={(event) => onDomain(event.target.value)}
              placeholder="portal.youragency.com"
              className="h-10 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <Button
          variant={verified ? 'outline' : 'primary'}
          icon={verified ? 'ri-refresh-line' : 'ri-shield-check-line'}
          disabled={!canEdit}
          onClick={onVerify}
        >
          {verified ? 'Re-check' : 'Verify domain'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 flex items-start gap-1.5 text-xs text-primary-700">
          <i className="ri-error-warning-line mt-px" />
          {error}
        </p>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
          <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
            DNS
          </p>
          <p className="mt-1 text-sm font-medium text-foreground-900">
            {verified ? 'Propagated' : 'Awaiting records'}
          </p>
        </div>
        <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
          <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
            SSL certificate
          </p>
          <p className="mt-1 text-sm font-medium text-foreground-900">
            {verified ? 'Active · auto-renew' : 'Issued after DNS'}
          </p>
        </div>
        <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
          <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
            Status
          </p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-foreground-900">
            <i className={verified ? 'ri-lock-2-line text-accent-700' : 'ri-time-line text-secondary-700'} />
            {verified ? 'HTTPS live' : 'Pending verification'}
          </p>
        </div>
      </div>

      <div className="mt-5 border-t border-background-100 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-label font-medium text-foreground-700">
            DNS records to add at your registrar
          </p>
          <Badge tone="outline" icon="ri-information-line">
            Changes can take up to 24 hours
          </Badge>
        </div>

        <div className="mt-2 overflow-hidden rounded-md border border-background-200">
          <div className="hidden grid-cols-[80px_minmax(0,1fr)_minmax(0,1.4fr)_60px_40px] items-center gap-3 border-b border-background-200 bg-background-100 px-3 py-2 text-[11px] font-label uppercase tracking-wider text-foreground-400 sm:grid">
            <span>Type</span>
            <span>Name</span>
            <span>Value</span>
            <span>TTL</span>
            <span className="text-right">Copy</span>
          </div>
          <ul className="divide-y divide-background-100">
            {dnsRecords.map((record) => (
              <li
                key={record.id}
                className="grid grid-cols-1 gap-1.5 px-3 py-2.5 sm:grid-cols-[80px_minmax(0,1fr)_minmax(0,1.4fr)_60px_40px] sm:items-center sm:gap-3"
              >
                <span className="inline-flex w-fit items-center rounded border border-background-300 bg-background-100 px-1.5 py-0.5 text-[11px] font-label font-medium text-foreground-700">
                  {record.type}
                </span>
                <span className="truncate font-mono text-xs text-foreground-800">
                  {record.name}
                </span>
                <span className="truncate font-mono text-xs text-foreground-600">
                  {record.value}
                </span>
                <span className="text-xs text-foreground-500">{record.ttl}</span>
                <span className="sm:text-right">
                  <button
                    type="button"
                    onClick={() => copy(record.id, record.value)}
                    aria-label={`Copy ${record.type} value`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 transition-colors cursor-pointer hover:bg-background-200 hover:text-foreground-900"
                  >
                    <i className={copied === record.id ? 'ri-check-line text-accent-700' : 'ri-file-copy-line'} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SectionCard>
  );
}